import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TelegramLinkCard } from '@/components/TelegramLinkCard';
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuthStore, useStudioStore } from '@/store';
import { softRefresh, hardReset } from '@/lib/appRefresh';
import { sendFeedbackToDeveloper } from '@/lib/feedback';
import { supabase } from '@/lib/supabase';
import { callAuth } from '@/lib/authClient';
import { Card } from '@/components/ui/Components';
import { Button } from '@/components/ui/Button';
import { AlertCircle, RotateCcw, MessageSquare, Mail, CheckCircle2, Clock, Send, User, LogOut, UserPlus, ChevronRight } from 'lucide-react';

export const ManagementCompanyPageSimple: React.FC = () => {
  const { user: authUser } = useAuthStore();
  const { studios } = useStudioStore();
  const { user: telegramUser, showAlert, hapticFeedback } = useTelegram();
  const { getAccessLevel, loading: checkingAccess } = useManagementCompanySimple(authUser?.id);
  const navigate = useNavigate();
  const [accessLevel, setAccessLevel] = useState<'standard' | 'advanced' | null | undefined>(undefined);
  const [isRefreshingApp, setIsRefreshingApp] = useState(false);

  // Email management state
  const [profileEmail, setProfileEmail] = useState(authUser?.email || '');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailResending, setEmailResending] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const { login } = useAuthStore();

  // Profile name state
  const [isEditingName, setIsEditingName] = useState(false);
  const [firstName, setFirstName] = useState(authUser?.first_name || '');
  const [lastName, setLastName] = useState(authUser?.last_name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const normalizedProfileEmail = profileEmail.trim().toLowerCase();
  const isProfileEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedProfileEmail);
  const isEmailVerified = Boolean(authUser?.email && authUser?.email_verified_at);
  const isEmailPending = Boolean(authUser?.email && !authUser?.email_verified_at);
  const isPendingView = isEmailPending && !isEditingEmail;

  useEffect(() => {
    if (authUser?.email) setProfileEmail(authUser.email);
  }, [authUser?.email]);

  useEffect(() => {
    setFirstName(authUser?.first_name || '');
    setLastName(authUser?.last_name || '');
  }, [authUser?.first_name, authUser?.last_name]);

  const handleSaveName = async () => {
    const canEditOwnName = studios.some(studio => studio.role === 'owner' || studio.access_kind === 'owner');
    if (!canEditOwnName) {
      setNameError('Изменить имя может только владелец');
      setIsEditingName(false);
      return;
    }

    setNameSaving(true);
    setNameError(null);
    try {
      await (supabase as any).rpc('update_user_profile', {
        p_first_name: firstName.trim(),
        p_last_name: lastName.trim()
      });
      if (authUser) {
        login({
          ...authUser,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          updated_at: new Date().toISOString()
        });
      }
      setIsEditingName(false);
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!normalizedProfileEmail || !isProfileEmailValid) {
      setEmailError('Проверьте формат email');
      return;
    }
    setEmailSaving(true);
    setEmailError(null);

    try {
      await callAuth('link-email', { email: normalizedProfileEmail });

      if (authUser) {
        login({
          ...authUser,
          email: normalizedProfileEmail,
          email_verified_at: null,
          auth_method: authUser.auth_method,
          updated_at: new Date().toISOString()
        });
      }
      setEmailVerificationSent(true);
      setIsEditingEmail(false);
    } catch (err) {
      setEmailVerificationSent(false);
      setEmailError(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setEmailSaving(false);
      setEmailResending(false);
    }
  };

  const handleAppRefresh = async () => {
    if (isRefreshingApp) return;
    if (!confirm('Обновить приложение? Кэш будет очищен, вы останетесь в системе.')) return;

    setIsRefreshingApp(true);

    try {
      await softRefresh();
    } catch (error) {
      console.error('❌ ManagementCompanyPageSimple: failed to refresh app:', error);
      setIsRefreshingApp(false);
    }
  };

  const handleLogout = async () => {
    if (isRefreshingApp) return;
    if (!confirm('Выйти из аккаунта? Приложение очистит токен и кэш, после чего потребуется повторный вход.')) return;

    setIsRefreshingApp(true);

    try {
      await hardReset();
    } catch (error) {
      console.error('❌ ManagementCompanyPageSimple: failed to logout:', error);
      setIsRefreshingApp(false);
    }
  };

  console.log('🔍 ManagementCompanyPageSimple render:', {
    authUserId: authUser?.id,
    telegramUserId: telegramUser?.id,
    checkingAccess,
    accessLevel
  });

  useEffect(() => {
    const checkAccess = async () => {
      console.log('🚀 ManagementCompanyPageSimple: Starting access check');
      if (authUser?.id) {
        console.log('📞 ManagementCompanyPageSimple: Calling getAccessLevel for:', authUser.id);
        const level = await getAccessLevel();
        console.log('✅ ManagementCompanyPageSimple: Access level result:', level);
        setAccessLevel(level);
      } else {
        console.log('❌ ManagementCompanyPageSimple: No user id found in authUser');
        setAccessLevel(null);
      }
    };

    checkAccess();
  }, [authUser, getAccessLevel]);

  if (checkingAccess || accessLevel === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500">Проверка доступа...</p>
        </div>
      </div>
    );
  }

  if (accessLevel === null) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <div className="p-6 text-center space-y-4">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold">Доступ запрещен</h2>
            <p className="text-gray-500">
              У вас нет доступа к управляющей компании. 
              Для получения доступа обратитесь к администратору.
            </p>
            <div className="bg-gray-100 p-3 rounded-lg">
              <p className="text-sm">
                <strong>Требуется:</strong> Доступ к управляющей компании
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Кнопки доступны для всех пользователей УК (standard и advanced)
  const pageHeader = (
    <div className="mb-4">
      <h1 className="text-2xl font-bold">Настройки</h1>
      <p className="text-sm text-gray-500">Профиль, способы входа и доступы управляющей компании</p>
    </div>
  );

  const utilityButtons = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div
        onClick={() => {
          try {
            hapticFeedback.impact('light')
            sendFeedbackToDeveloper()
          } catch (error) {
            console.error('Failed to send feedback:', error)
            showAlert('Ошибка отправки репорта')
          }
        }}
        className="cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
      >
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">Обратная связь</p>
              <p className="text-xs text-muted-foreground">
                Сообщите о проблеме или предложите улучшение
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div
        onClick={() => void handleAppRefresh()}
        className={`transition-all ${isRefreshingApp ? 'cursor-wait opacity-80' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`}
      >
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <RotateCcw className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">Обновить приложение</p>
              <p className="text-xs text-muted-foreground">
                {isRefreshingApp ? 'Обновляем...' : 'Очистка кэша и перезагрузка приложения'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div
        onClick={() => void handleLogout()}
        className={`transition-all ${isRefreshingApp ? 'cursor-wait opacity-80' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]'}`}
      >
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">Выйти из аккаунта</p>
              <p className="text-xs text-muted-foreground">
                {isRefreshingApp ? 'Выходим...' : 'Завершение сеанса и повторный вход'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const displayName = [authUser?.first_name, authUser?.last_name].filter(Boolean).join(' ');
  const canEditOwnName = studios.some(studio => studio.role === 'owner' || studio.access_kind === 'owner');

  const profileNameSection = (
    <Card className="mb-6 p-4">
      <div className="flex items-center gap-2 mb-3">
        <User className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-base">Профиль</h3>
      </div>

      {isEditingName && canEditOwnName ? (
        <div className="space-y-2">
          <input
            type="text"
            value={firstName}
            onChange={e => { setFirstName(e.target.value); setNameError(null); }}
            placeholder="Имя"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            disabled={nameSaving}
          />
          <input
            type="text"
            value={lastName}
            onChange={e => { setLastName(e.target.value); setNameError(null); }}
            placeholder="Фамилия"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            disabled={nameSaving}
          />
          {nameError && <p className="text-sm text-red-600">{nameError}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSaveName()} disabled={nameSaving}>
              {nameSaving ? 'Сохраняем...' : 'Сохранить'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsEditingName(false);
                setFirstName(authUser?.first_name || '');
                setLastName(authUser?.last_name || '');
                setNameError(null);
              }}
              disabled={nameSaving}
            >
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            {displayName ? (
              <p className="text-sm font-medium">{displayName}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Имя не указано</p>
            )}
            {authUser?.tg_username && (
              <p className="text-xs text-muted-foreground">Telegram: @{authUser.tg_username}</p>
            )}
            {authUser?.email && (
              <p className="text-xs text-muted-foreground">Email: {authUser.email}</p>
            )}
          </div>
          {canEditOwnName && (
            <Button variant="outline" size="sm" onClick={() => setIsEditingName(true)}>
              Изменить имя
            </Button>
          )}
        </div>
      )}
    </Card>
  );

  const emailSection = (
    <Card className="mb-6 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-base">Управление профилем — Email</h3>
      </div>

      {/* Status badge */}
      {isEmailVerified ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-700 text-sm">
            Подтвержден: <span className="font-medium">{authUser?.email}</span>
          </span>
        </div>
      ) : isEmailPending ? (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3">
          <Clock className="w-4 h-4 text-amber-600" />
          <span className="text-amber-700 text-sm">
            Не подтвержден: <span className="font-medium">{authUser?.email}</span>
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mb-3">
          Привяжите email для резервного входа без Telegram.
        </p>
      )}

      {emailVerificationSent && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 mb-3">
          <Send className="w-4 h-4 text-emerald-600 mt-0.5" />
          <span className="text-emerald-700 text-sm">
            Ссылка для подтверждения отправлена на <span className="font-medium">{normalizedProfileEmail || authUser?.email}</span>.
            Проверьте почту.
          </span>
        </div>
      )}

      {emailError && (
        <p className="text-sm text-red-600 mb-3">{emailError}</p>
      )}

      {isPendingView && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => void handleSaveEmail()}
              disabled={emailSaving || emailResending}
            >
              {emailResending ? 'Отправляем...' : 'Отправить ссылку снова'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setIsEditingEmail(true);
                setProfileEmail(authUser?.email || '');
                setEmailVerificationSent(false);
                setEmailError(null);
              }}
              disabled={emailSaving || emailResending}
            >
              Изменить email
            </Button>
          </div>
        </div>
      )}

      {/* Email input (new email link or edit mode) */}
      {(!isEmailVerified || isEditingEmail) && !isPendingView && (
        <div className="space-y-2">
          <input
            type="email"
            value={profileEmail}
            onChange={(e) => {
              setProfileEmail(e.target.value);
              setEmailError(null);
              setEmailVerificationSent(false);
            }}
            placeholder="you@example.com"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            disabled={emailSaving || emailResending}
          />
          {profileEmail.length > 0 && !isProfileEmailValid && (
            <p className="text-xs text-red-600">Проверьте формат email</p>
          )}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => void handleSaveEmail()}
              disabled={
                emailSaving ||
                !isProfileEmailValid ||
                normalizedProfileEmail === (authUser?.email || '').trim().toLowerCase()
              }
            >
              {emailSaving ? 'Сохраняем...' : 'Сохранить и отправить ссылку'}
            </Button>
            {isEditingEmail && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditingEmail(false);
                  setProfileEmail(authUser?.email || '');
                  setEmailVerificationSent(false);
                  setEmailError(null);
                }}
                disabled={emailSaving || emailResending}
              >
                Отмена
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Verified — option to change */}
      {isEmailVerified && !isEditingEmail && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setIsEditingEmail(true);
            setProfileEmail(authUser?.email || '');
            setEmailVerificationSent(false);
            setEmailError(null);
          }}
        >
          Изменить email
        </Button>
      )}
    </Card>
  );

  const telegramSection = (
    <TelegramLinkCard className="mb-6" title="Управление профилем - Telegram" />
  );

  const versionInfo = (
    <div className="text-center text-xs text-muted-foreground mb-6">
      Версия приложения: {(import.meta.env.VITE_APP_VERSION || '1.0.0').slice(0, 7)}
      {localStorage.getItem('last_cache_clear') && (
        <span className="ml-2">
          • Последняя очистка кэша: {new Date(localStorage.getItem('last_cache_clear')!).toLocaleDateString('ru-RU')}
        </span>
      )}
    </div>
  );

  // Выдача доступов переехала с этого экрана 30.08.2026: настройки — личный
  // профиль, а не место, где заводят сотрудников. Здесь остаётся вход в заявку,
  // и он общий для обоих уровней: самообслуживания больше нет ни у кого, кроме
  // супер-админа, поэтому advanced просит доступ так же, как standard.
  const accessRequestCard = (
    <div
      onClick={() => navigate('/management/access')}
      className="cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
    >
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <UserPlus className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1">Заявка на доступ в УК</p>
            <p className="text-xs text-muted-foreground">
              Попросить завести сотрудника — приглашение выдаст супер-админ
            </p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
        </div>
      </Card>
    </div>
  );

  // Стандартный доступ - только кнопки, без управления
  if (accessLevel === 'standard') {
    return (
      <div className="container mx-auto p-4">
        {pageHeader}
        {utilityButtons}
        {profileNameSection}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {emailSection}
          {telegramSection}
        </div>
        {versionInfo}
        
        {accessRequestCard}
      </div>
    );
  }

  // Расширенный доступ - полный функционал
  return (
    <div className="container mx-auto p-4">
      {pageHeader}
      {utilityButtons}
      {profileNameSection}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {emailSection}
        {telegramSection}
      </div>
      {versionInfo}

      {accessRequestCard}
    </div>
  );
};
